import React from 'react';
import { ScrollView, View } from 'react-native';
import { Text } from '@/components/ui/text';
import { DismissibleModal } from '@/components/ui/dismissible-modal';
import {
  FACET_LOAD_ERROR,
  useSetupFacetOptions,
} from '@/features/setup/hooks/use-setup-facet-options';
import { FacetChipGrid, type FacetOption } from '@/features/taste-profile/components/facet-chip-grid';
import { useTasteProfile } from '@/features/taste-profile/hooks/use-taste-profile';
import { useExistingUserPrompt } from '../hooks/use-existing-user-prompt';

interface ExistingUserTastePromptProps {
  visible: boolean;
  onClose: () => void;
}

interface PromptFacetProps {
  title: string;
  options: FacetOption[];
  hasError: boolean;
  isSelected: (option: FacetOption) => boolean;
  onToggle: (option: FacetOption) => void;
  testIdPrefix: string;
}

/** One labelled facet; a facet that failed to load says so instead of showing nothing. */
function PromptFacet({
  title,
  options,
  hasError,
  isSelected,
  onToggle,
  testIdPrefix,
}: PromptFacetProps) {
  return (
    <>
      <Text className="text-base font-semibold text-foreground mb-2">{title}</Text>
      <View className="mb-4">
        {hasError ? (
          <Text testID={`${testIdPrefix}-error`} className="text-sm text-muted-foreground">
            {FACET_LOAD_ERROR}
          </Text>
        ) : (
          <FacetChipGrid
            options={options}
            isSelected={isSelected}
            onToggle={onToggle}
            testIdPrefix={testIdPrefix}
          />
        )}
      </View>
    </>
  );
}

export function ExistingUserTastePrompt({ visible, onClose }: ExistingUserTastePromptProps) {
  const { save, dismiss } = useExistingUserPrompt();
  const { data, toggleCuisine, toggleAllergen, toggleDietary } = useTasteProfile();
  const { cuisines, allergens, dietaryProfiles, errors } = useSetupFacetOptions();

  const cuisineSelected = (o: FacetOption) =>
    data.cuisines.some((c) => c.id === o.id && c.valence === 'favorite');
  const allergenSelected = (o: FacetOption) => data.allergens.some((a) => a.id === o.id);
  const dietarySelected = (o: FacetOption) => data.dietaryProfiles.some((d) => d.id === o.id);

  const handleSave = async () => {
    await save();
    onClose();
  };

  const handleClose = async () => {
    await dismiss();
    onClose();
  };

  return (
    <DismissibleModal
      visible={visible}
      onClose={handleClose}
      title="Set your taste preferences"
      primaryAction={{ label: 'Save Preferences', onPress: handleSave, disabled: false }}
    >
      <ScrollView style={{ maxHeight: 400 }} showsVerticalScrollIndicator={false}>
        <Text className="text-sm text-muted-foreground mb-3">
          We&apos;ve added taste preferences. Pick a few favorites, allergies, and how you eat to
          personalize your experience.
        </Text>

        <PromptFacet
          title="Favorite cuisines"
          options={cuisines}
          hasError={errors.cuisines}
          isSelected={cuisineSelected}
          onToggle={(o) => toggleCuisine(o, 'favorite')}
          testIdPrefix="onboarding-cuisine"
        />

        <PromptFacet
          title="Any allergies?"
          options={allergens}
          hasError={errors.allergens}
          isSelected={allergenSelected}
          onToggle={toggleAllergen}
          testIdPrefix="onboarding-allergen"
        />

        <PromptFacet
          title="How you eat"
          options={dietaryProfiles}
          hasError={errors.dietaryProfiles}
          isSelected={dietarySelected}
          onToggle={toggleDietary}
          testIdPrefix="onboarding-dietary"
        />
      </ScrollView>
    </DismissibleModal>
  );
}
